import { createRoot } from 'react-dom/client';
import { IntlProvider, type IntlShape } from 'react-intl';

import { AdControlsFlag } from '../components/dismissalFlag';

const pendingCleanups = new Set<() => void>();

/**
 * Renders the dismissal flag in its own React root attached to
 * `document.body`, outside the message's own component tree. Dismissing/snoozing a message
 * unmounts that tree immediately, which would otherwise tear the flag down with it before the
 * user ever sees it.
 */
export const showStandaloneAdControlsFlag = (messageInstanceId: string, intl: IntlShape): void => {
	const container = globalThis.document.createElement('div');
	container.dataset.testid = 'post-office-standalone-ad-controls-flag-container';
	globalThis.document.body.appendChild(container);
	const root = createRoot(container);

	const cleanup = () => {
		pendingCleanups.delete(cleanup);
		root.unmount();
		container.remove();
	};
	pendingCleanups.add(cleanup);

	root.render(
		<IntlProvider locale={intl.locale} messages={intl.messages} onError={intl.onError}>
			<AdControlsFlag messageInstanceId={messageInstanceId} onCloseFlag={cleanup} />
		</IntlProvider>,
	);
};

/**
 * Test-only: unmounts any standalone flags left over from a test that didn't wait for the
 * flag's own auto-dismiss/close. Removing the container node directly (without calling
 * `root.unmount()`) would leave React portals rendered by the flag's own internals (e.g. its
 * z-index/portal wrapper) orphaned in the document, leaking into the next test.
 */
export const resetStandaloneAdControlsFlagsForTests = (): void => {
	pendingCleanups.forEach((cleanup) => cleanup());
};
